import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Loader2, Clock, AlertCircle, Lock, ShieldCheck, Tag, Trophy, X, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { fetchApi, formatDate } from '../lib/utils';

// Exit survey reasons
const EXIT_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_ready', label: 'Not ready to commit' },
  { id: 'found_alternative', label: 'Found another solution' },
  { id: 'just_browsing', label: 'Just browsing' },
  { id: 'other', label: 'Other reason' },
];

// Initialize Stripe lazily to prevent loading errors
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    const { loadStripe } = require('@stripe/stripe-js');
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  
  // Exit survey state
  const [showExitSurvey, setShowExitSurvey] = useState(false);
  const [exitReason, setExitReason] = useState('');
  const [exitOther, setExitOther] = useState('');
  const [submittingExitSurvey, setSubmittingExitSurvey] = useState(false);
  
  // Check if coming from challenge page
  const isChallenge = searchParams.get('challenge') === 'true';

  useEffect(() => {
    loadPlans();
    loadCurrentSubscription();

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadPlans = async () => {
    try {
      const data = await fetchApi('/subscription/plans');
      setPlans(data.plans || []);
      setTrialDays(data.trial_days || 7);
    } catch (error) {
      console.error('Load plans error:', error);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const data = await fetchApi('/subscription/current');
      setCurrentSubscription(data);
    } catch (error) {
      console.error('Load subscription error:', error);
    } finally {
      setLoadingSub(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const status = await fetchApi(`/subscription/status/${sessionId}`);

        if (status.status === 'complete') {
          updateUser({
            subscription_status: status.subscription_status || 'trialing',
            subscription_plan: selectedPlan?.id,
          });
          navigate('/subscription/success');
          return;
        }

        if (status.status === 'expired') {
          navigate('/subscription/cancel');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setCheckingStatus(false);
          loadCurrentSubscription();
        }
      } catch (error) {
        console.error('Status check error:', error);
        setCheckingStatus(false);
      }
    };

    poll();
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setApplyingPromo(true);
    setPromoError('');
    setPromoApplied(null);
    
    try {
      const response = await fetchApi('/subscription/validate-promo', {
        method: 'POST',
        body: JSON.stringify({ code: promoCode.trim().toUpperCase() }),
      });
      
      if (response.valid) {
        setPromoApplied(response);
        setPromoError('');
      } else {
        setPromoError(response.message || 'Invalid promo code');
        setPromoApplied(null);
      }
    } catch (error) {
      setPromoError(error.message || 'Could not validate promo code');
      setPromoApplied(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setLoading(true);
    
    try {
      // Go directly to Stripe Checkout - no need for our own card form
      const response = await fetchApi('/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: plan.id,
          origin_url: window.location.origin,
          promo_code: promoApplied?.code || null,
          challenge_registration: isChallenge, // Register for challenge if coming from challenge page
        }),
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      // Fallback for demo mode
      if (response.status === 'success' && response.redirect_url) {
        updateUser({ subscription_status: 'trialing', subscription_plan: plan.id });
        navigate('/subscription/success');
        return;
      }
    } catch (err) {
      alert(err.message || 'Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    updateUser({ subscription_status: 'trialing', subscription_plan: selectedPlan?.id });
    navigate('/subscription/success');
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel?\n\nYou will keep full access until the end of your current billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      const response = await fetchApi('/subscription/cancel', { method: 'POST' });
      const accessDate = response.access_until ? new Date(response.access_until).toLocaleDateString() : 'the end of your period';
      alert(`Subscription cancelled.\n\nYou'll have full access until ${accessDate}.`);
      await loadCurrentSubscription();
    } catch (error) {
      console.error('Cancel error:', error);
      alert(error.message || 'Could not cancel subscription. Please contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgrade = async (newPlan) => {
    if (!confirm(`Upgrade to ${newPlan.name} plan for $${newPlan.price}/${newPlan.interval}?`)) {
      return;
    }

    setUpgrading(true);
    try {
      const response = await fetchApi('/subscription/upgrade', {
        method: 'POST',
        body: JSON.stringify({ new_plan_id: newPlan.id }),
      });
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        alert(response.message || 'Plan updated successfully!');
        await loadCurrentSubscription();
        setShowUpgradeOptions(false);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert(error.message || 'Could not upgrade subscription. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleReactivate = async () => {
    setCancelling(true);
    try {
      await fetchApi('/subscription/reactivate', { method: 'POST' });
      loadCurrentSubscription();
    } catch (error) {
      console.error('Reactivate error:', error);
    } finally {
      setCancelling(false);
    }
  };

  const handleExitClick = () => {
    // Only show exit survey for non-subscribed users
    if (!hasActiveSubscription) {
      setShowExitSurvey(true);
    } else {
      navigate('/');
    }
  };

  const submitExitSurvey = async () => {
    if (!exitReason) return;
    setSubmittingExitSurvey(true);
    try {
      await fetchApi('/exit-survey/submit', {
        method: 'POST',
        body: JSON.stringify({
          reason: exitReason,
          other_details: exitReason === 'other' ? exitOther : null,
        }),
      });
    } catch (err) {
      console.error('Exit survey error:', err);
    } finally {
      setSubmittingExitSurvey(false);
      setShowExitSurvey(false);
      navigate('/');
    }
  };

  const skipExitSurvey = () => {
    setShowExitSurvey(false);
    navigate('/');
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#CCFF00] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Setting up your subscription...</p>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = currentSubscription?.status === 'active' || currentSubscription?.status === 'trialing';
  const isTrialing = currentSubscription?.status === 'trialing';
  const isCancelPending = currentSubscription?.cancel_at_period_end;
  const canGoBack = hasActiveSubscription;

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />

      {/* Exit Survey Modal */}
      <AnimatePresence>
        {showExitSurvey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-800 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#CCFF00]" />
                  Quick Feedback
                </h3>
                <button onClick={skipExitSurvey} className="text-zinc-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-zinc-400 mb-4">Help us improve! Why are you leaving?</p>
              
              <div className="space-y-2 mb-4">
                {EXIT_REASONS.map((reason) => (
                  <button
                    key={reason.id}
                    onClick={() => setExitReason(reason.id)}
                    className={`w-full p-3 rounded-xl text-left transition-colors ${
                      exitReason === reason.id
                        ? 'bg-[#CCFF00]/20 border-[#CCFF00] border text-white'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-600'
                    }`}
                    data-testid={`exit-reason-${reason.id}`}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
              
              {exitReason === 'other' && (
                <Input
                  placeholder="Please tell us more..."
                  value={exitOther}
                  onChange={(e) => setExitOther(e.target.value)}
                  className="mb-4 bg-zinc-800 border-zinc-700"
                  data-testid="exit-other-input"
                />
              )}
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={skipExitSurvey}
                  className="flex-1 border-zinc-700"
                >
                  Skip
                </Button>
                <Button
                  onClick={submitExitSurvey}
                  disabled={!exitReason || submittingExitSurvey}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                  data-testid="submit-exit-survey-btn"
                >
                  {submittingExitSurvey ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          {canGoBack ? (
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
          ) : (
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={handleExitClick}
              data-testid="logo-home"
            >
              <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black">V</span>
              </div>
              <span className="font-bold">VYTA</span>
            </div>
          )}
          <h1 className="text-lg font-bold">{hasActiveSubscription ? 'Subscription' : 'Start Your Trial'}</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Current Subscription Status */}
        {!loadingSub && hasActiveSubscription && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`card-default mb-8 ${isTrialing ? 'border-[#F59E0B]/30' : 'border-[#CCFF00]/30'}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isTrialing ? 'bg-[#F59E0B]/10' : 'bg-[#CCFF00]/10'}`}>
                  {isTrialing ? <Clock className="w-6 h-6 text-[#F59E0B]" /> : <Check className="w-6 h-6 text-[#CCFF00]" />}
                </div>
                <div>
                  <h3 className="font-bold">{isTrialing ? 'Free Trial Active' : 'Subscription Active'}</h3>
                  <p className="text-sm text-zinc-400">{currentSubscription.plan?.charAt(0).toUpperCase() + currentSubscription.plan?.slice(1)} plan</p>
                </div>
              </div>

              {isCancelPending ? (
                <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-bold">Access until {formatDate(currentSubscription.current_period_end)}</span>
              ) : isTrialing && (
                <span className="px-3 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold">{currentSubscription.trial_days_remaining} days left</span>
              )}
            </div>

            {isCancelPending && (
              <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-400">Subscription cancelled</p>
                    <p className="text-xs text-zinc-400 mt-1">You&apos;ll have full access until {formatDate(currentSubscription.current_period_end)}. After that, you&apos;ll need to resubscribe to continue.</p>
                  </div>
                </div>
              </div>
            )}

            {isTrialing && !isCancelPending && (
              <div className="mt-4 p-4 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#F59E0B]">Trial ends {formatDate(currentSubscription.trial_end)}</p>
                    <p className="text-xs text-zinc-400 mt-1">Your card will be charged automatically after the trial. Cancel anytime before to avoid charges.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-3">
              {isCancelPending ? (
                <Button onClick={handleReactivate} disabled={cancelling} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full" data-testid="reactivate-btn">
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reactivate Subscription'}
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowUpgradeOptions(!showUpgradeOptions)} 
                    className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 rounded-full"
                    data-testid="change-plan-btn"
                  >
                    {showUpgradeOptions ? 'Hide Plans' : 'Change Plan'}
                  </Button>
                  <Button variant="outline" onClick={handleCancel} disabled={cancelling} className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" data-testid="cancel-btn">
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Subscription'}
                  </Button>
                </>
              )}
            </div>

            {/* Upgrade Options */}
            {showUpgradeOptions && !isCancelPending && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                className="mt-6 pt-6 border-t border-zinc-800"
              >
                <h4 className="font-bold mb-4">Available Plans</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  {plans.map((plan) => {
                    const isCurrentPlan = currentSubscription?.plan === plan.id;
                    return (
                      <div 
                        key={plan.id} 
                        className={`p-4 rounded-xl border ${isCurrentPlan ? 'border-[#CCFF00] bg-[#CCFF00]/5' : 'border-zinc-800 bg-zinc-900/50'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-bold">{plan.name}</h5>
                          {isCurrentPlan && (
                            <span className="px-2 py-0.5 rounded-full bg-[#CCFF00]/20 text-[#CCFF00] text-xs font-bold">Current</span>
                          )}
                        </div>
                        <p className="text-2xl font-black mb-1">${plan.price}<span className="text-sm text-zinc-400 font-normal">/{plan.interval}</span></p>
                        {plan.discount_percent > 0 && (
                          <p className="text-xs text-[#14B8A6] mb-3">Save {plan.discount_percent}%</p>
                        )}
                        {!isCurrentPlan && (
                          <Button 
                            onClick={() => handleUpgrade(plan)} 
                            disabled={upgrading}
                            size="sm"
                            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-full mt-2"
                          >
                            {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Switch to This Plan'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Plans Section */}
        {!hasActiveSubscription && (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
              <h2 className="text-3xl font-black mb-2">{isChallenge ? 'Join the Challenge!' : 'One Last Step!'}</h2>
              <p className="text-zinc-400">Start your {trialDays}-day free trial to access your personalized programs</p>
            </motion.div>

            {/* Challenge Registration Banner */}
            {isChallenge && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.05 }}
                className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-yellow-400/10 via-amber-500/10 to-yellow-400/10 border border-yellow-400/30"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-7 h-7 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-yellow-400 mb-1">🏆 Vyta-Fit 5 Week Challenge</h3>
                    <p className="text-sm text-zinc-300 mb-2">
                      You're signing up for the <strong>Global Transformation Challenge</strong> with a chance to win a share of <strong className="text-yellow-400">USD $2,000</strong>!
                    </p>
                    <p className="text-sm text-zinc-400">
                      Your 7-day free trial still applies — you won't be charged until after your trial ends.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Trial Info Banner */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-[#CCFF00]/10 to-[#14B8A6]/10 border border-[#CCFF00]/20">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#CCFF00]/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-7 h-7 text-[#CCFF00]" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{promoApplied?.trial_days || trialDays}-Day Free Trial</h3>
                  <p className="text-sm text-zinc-400">Your card won&apos;t be charged until your trial ends. Cancel anytime before to avoid charges — your subscription continues automatically after the trial.</p>
                </div>
              </div>
            </motion.div>

            {/* Promo Code Section */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-8">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoError('');
                      if (!e.target.value) setPromoApplied(null);
                    }}
                    className="pl-10 bg-zinc-900 border-zinc-800 uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <Button
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || applyingPromo}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800"
                  data-testid="apply-promo-btn"
                >
                  {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
              {promoError && (
                <p className="text-red-400 text-sm mt-2">{promoError}</p>
              )}
              {promoApplied && (
                <div className="mt-3 p-3 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/20">
                  <p className="text-[#14B8A6] text-sm font-medium flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {promoApplied.description}
                  </p>
                </div>
              )}
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan, i) => (
                <motion.div 
                  key={plan.id} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.2 + i * 0.1 }} 
                  className={`subscription-card relative ${
                    plan.id === 'quarterly' 
                      ? 'featured border-[#CCFF00] scale-105' 
                      : plan.id === 'yearly'
                      ? 'border-emerald-500/50'
                      : ''
                  }`} 
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.id === 'quarterly' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#CCFF00] text-black text-xs font-bold whitespace-nowrap">
                      Most Popular
                    </div>
                  )}
                  {plan.id === 'yearly' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold whitespace-nowrap">
                      Best Value
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold mb-1 mt-2">{plan.name}</h3>
                  
                  {plan.discount_percent > 0 && (
                    <div className="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold mb-2">
                      Save {plan.discount_percent}%
                    </div>
                  )}
                  
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-black">${plan.price}</span>
                    <span className="text-zinc-400 text-sm">/{plan.interval}</span>
                  </div>
                  <p className="text-sm text-zinc-500 mb-4">after {trialDays}-day free trial</p>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Full program access</li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Personalized nutrition</li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Progress tracking</li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Monthly challenges</li>
                  </ul>

                  <Button 
                    onClick={() => handleSelectPlan(plan)} 
                    disabled={loading}
                    className={`w-full rounded-full font-bold ${
                      plan.id === 'quarterly' 
                        ? 'bg-[#CCFF00] text-black hover:bg-[#B3E600]' 
                        : plan.id === 'yearly'
                        ? 'bg-emerald-500 text-white hover:bg-emerald-400'
                        : 'bg-zinc-800 text-white hover:bg-zinc-700'
                    }`} 
                    data-testid={`select-${plan.id}-btn`}
                  >
                    {loading && selectedPlan?.id === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Start Free Trial'
                    )}
                  </Button>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Payment Info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
          <div className="flex items-center justify-center gap-4 text-zinc-500 text-sm">
            <Lock className="w-4 h-4" />
            <span>Secure payment powered by Stripe • Cancel anytime</span>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
